import React, { useState, useEffect } from "react";
import { Form, Field } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import { useQuery } from "@tanstack/react-query";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import AsyncSelect from "../../AsyncSelect";
import InputField from "../../InputField";
import Button from "../../Button";
import {
  formatNumberWithCurrency,
  findItemById,
} from "../../../../utils/utils";
import { getItemOptions } from "../../../../services/Handlers/MasterItemsService";
import { paymentItemType } from "../../../../utils/constants";
import styles from "../ModalDetailReservationV2.module.css";

const emptyPaymentItem = {
  item: null,
  qty: 1,
  amount: null,
};

const TablePaymentCorrectionItem = ({
  fields,
  itemOptions = [],
  disabledForm = false,
  handleChangeItem,
  handleRemoveItem,
  paymentCorrectionData = [],
}) => {
  const localize = useLocalization();

  const requiredField = (value, label) => {
    if (!value) {
      return localize.getText("generalRequiredFieldValidation", {
        fieldName: label,
      });
    }
    return undefined;
  };

  return (
    <div className={styles.paymentTable}>
      <div className={styles.tableHeader}>
        <p className={`${styles.tableHeaderItem} ${styles.tableHeaderName}`}>
          {localize.getText("name")}
        </p>
        <p
          className={`${styles.tableHeaderItem} ${styles.tableHeaderQuantity}`}
        >
          {localize.getText("quantity")}
        </p>
        <p className={`${styles.tableHeaderItem} ${styles.tableHeaderPrice}`}>
          {localize.getText("price")}
        </p>
        <p style={{ width: 48 }}></p>
      </div>

      {fields.map((name, index) => {
        return (
          <div key={index} className={styles.tableRow}>
            <div className={styles.tableHeaderName}>
              <Field
                name={`${name}.item`}
                validate={(value) =>
                  requiredField(value, localize.getText("item"))
                }
              >
                {({ input, meta }) => (
                  <AsyncSelect
                    placeholder={localize.getText("chooseItem")}
                    size="small"
                    fullWidth
                    isGroup
                    {...input}
                    error={meta.touched && meta.error}
                    options={itemOptions}
                    labelKey="label"
                    valueKey="value"
                    disabled={disabledForm}
                    value={paymentCorrectionData[index]?.item}
                    onChange={(value) => {
                      input.onChange(value);
                      handleChangeItem(index, "item", value);
                    }}
                  />
                )}
              </Field>
            </div>

            <Field
              name={`${name}.qty`}
              validate={(value) =>
                requiredField(value, localize.getText("quantity"))
              }
            >
              {({ input, meta }) => (
                <InputField
                  placeholder="0"
                  type="number"
                  size="small"
                  classNameInput={styles.tableHeaderQuantity}
                  {...input}
                  error={meta.touched && meta.error}
                  disabled={disabledForm}
                  value={paymentCorrectionData[index]?.qty}
                  onChange={(value) => {
                    input.onChange(value);
                    handleChangeItem(index, "qty", value);
                  }}
                />
              )}
            </Field>

            <Field
              name={`${name}.amount`}
              validate={(value) =>
                requiredField(value, localize.getText("price"))
              }
            >
              {({ input, meta }) => (
                <InputField
                  placeholder="0"
                  type="number"
                  size="small"
                  classNameInput={styles.tableHeaderPrice}
                  {...input}
                  error={meta.touched && meta.error}
                  disabled={disabledForm}
                  value={paymentCorrectionData[index]?.amount}
                  onChange={(value) => {
                    input.onChange(value);
                    handleChangeItem(index, "amount", value);
                  }}
                />
              )}
            </Field>

            <Button
              variant="danger"
              size="sm"
              onClick={() => handleRemoveItem(index)}
              disabled={disabledForm}
            >
              <i className="fas fa-trash" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};

const PaymentCorrectionSection = ({
  businessId,
  paymentData = null,
  onSubmit = () => undefined,
  disabledForm = false,
  loading = false,
}) => {
  const localize = useLocalization();
  const [itemOptions, setItemOptions] = useState([]);
  const [filteredItemOptions, setFilteredItemOptions] = useState([]);
  const [paymentCorrectionData, setPaymentCorrectionData] = useState([]);

  useQuery(["GetItemsOption", businessId], () => getItemOptions(businessId), {
    onSuccess: (res) => {
      if (res.data && res.status === 200) {
        setItemOptions(res.data);
      }
    },
    enabled: !!businessId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (
      paymentData &&
      paymentCorrectionData.length === 0 &&
      itemOptions.length > 0
    ) {
      const paymentItems = [];

      if (
        paymentData.payment_items &&
        paymentData.payment_items.filter(
          (value) => value.type === paymentItemType.CORRECTION
        ).length > 0
      ) {
        paymentData.payment_items
          .filter((value) => value.type === paymentItemType.CORRECTION)
          .forEach((item) => {
            paymentItems.push({
              item: findItemById(itemOptions, item.item_id),
              qty: item.quantity,
              amount: item.amount,
            });
          });
      }

      setPaymentCorrectionData(paymentItems);
    }
  }, [paymentData, itemOptions, paymentCorrectionData.length]);

  useEffect(() => {
    if (itemOptions.length > 0 && paymentData?.payment_items) {
      const consultationItemIds = paymentData.payment_items
        .filter((item) => item.type === paymentItemType.CONSULTATION)
        .map((item) => item.item_id);

      const filtered = itemOptions
        .map((option) => {
          if (option.options) {
            const filteredChildren = option.options.filter((child) =>
              consultationItemIds.includes(child.value)
            );

            if (filteredChildren.length > 0) {
              return {
                ...option,
                options: filteredChildren,
              };
            }
            return null;
          } else {
            return consultationItemIds.includes(option.value) ? option : null;
          }
        })
        .filter(Boolean);

      setFilteredItemOptions(filtered);
    } else {
      setFilteredItemOptions([]);
    }
  }, [itemOptions, paymentData]);

  const handleAddPaymentItem = () => {
    const dataPaymentItems = [...paymentCorrectionData];
    dataPaymentItems.push(emptyPaymentItem);
    setPaymentCorrectionData(dataPaymentItems);
  };

  const handleRemoveItem = (index) => {
    const dataPaymentItems = [...paymentCorrectionData];
    dataPaymentItems.splice(index, 1);
    setPaymentCorrectionData(dataPaymentItems);
  };

  const handleChangeItem = (index, field, value) => {
    const dataPayment = JSON.parse(JSON.stringify([...paymentCorrectionData]));

    dataPayment.forEach((item, idx) => {
      if (idx === index) {
        if (field === "item") {
          const selectedItem = findItemById(itemOptions, value.value);
          item["amount"] = selectedItem.price;
        }
        item[field] = value;
      }
    });

    setPaymentCorrectionData(dataPayment);
  };

  const requiredArray = (value) =>
    value && value.length > 0 ? undefined : localize.getText("required");

  const handleSubmitPaymentCorrection = () => {
    const dataPayment =
      paymentData?.payment_items
        ?.filter((value) => value.type === paymentItemType.CONSULTATION)
        ?.map((item) => ({
          itemId: item.item_id,
          itemName: item.item_name_original || item.item_name,
          qty: item.quantity,
          amount: item.amount,
          typeDiscount: item.type_discount,
          discount: item.discount,
          type: item.type,
        })) || [];

    const dataPaymentCorrection = paymentCorrectionData.map((item) => ({
      ...item,
      itemId: item.item?.value,
      itemName: item.item?.label,
      type: paymentItemType.CORRECTION,
    }));

    const formattedData = {
      method: paymentData?.payment_method || "",
      cardNumber: paymentData?.card_number || "",
      totalAmount: paymentData?.total_amount || 0,
      totalAmountCorrection: totalAmount,
      paymentItems: [...dataPaymentCorrection, ...dataPayment],
    };

    onSubmit(formattedData);
  };

  const totalAmount = paymentCorrectionData
    .filter((item) => item.amount !== null)
    .reduce((sum, current) => {
      const amount = Number(current.amount) || 0;
      return sum + amount;
    }, 0);

  return (
    <Form
      onSubmit={handleSubmitPaymentCorrection}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={{ paymentItems: [...paymentCorrectionData] }}
      validate={() => ({})}
      enableReinitialize={true}
    >
      {({ handleSubmit }) => (
        <div className={styles.paymentSection}>
          <div className={styles.paymentContent}>
            <div className={styles.paymentHeader}>
              <i
                className={`far fa-money-bill-alt ${styles.paymentCorrectionIcon}`}
              />
              <p className={styles.paymentTitle}>
                {localize.getText("paymentCorrection")}
              </p>
            </div>

            {paymentCorrectionData.length > 0 ? (
              <FieldArray name="paymentItems" validate={requiredArray}>
                {({ fields }) => (
                  <TablePaymentCorrectionItem
                    fields={fields}
                    itemOptions={filteredItemOptions}
                    disabledForm={disabledForm}
                    paymentCorrectionData={paymentCorrectionData}
                    handleChangeItem={handleChangeItem}
                    handleRemoveItem={handleRemoveItem}
                  />
                )}
              </FieldArray>
            ) : (
              <div className="d-flex justify-content-center py-4">
                <Button
                  leftIcon={<i className="fas fa-plus" />}
                  label={localize.getText("paymentCorrection")}
                  variant="danger"
                  onClick={handleAddPaymentItem}
                  disabled={disabledForm}
                />
              </div>
            )}
          </div>

          <div className={styles.paymentFooter}>
            <div className={styles.paymentActions}>
              {paymentCorrectionData.length > 0 && (
                <div className={styles.addButtons}>
                  <Button
                    leftIcon={<i className="fas fa-plus" />}
                    label={localize.getText("item")}
                    variant="outline-light"
                    className={styles.addButton}
                    onClick={handleAddPaymentItem}
                    disabled={disabledForm}
                  />
                </div>
              )}

              <div className={styles.totalsSection}>
                <div className={styles.totalRow}>
                  <p className={styles.totalLabel}>
                    {localize.getText("totalCorrection")}:
                  </p>
                  <p className={styles.totalCorrectionValue}>
                    {formatNumberWithCurrency(totalAmount)}
                  </p>
                </div>
              </div>
            </div>

            {!disabledForm && paymentCorrectionData.length > 0 && (
              <Button
                label={localize.getText("save")}
                fullWidth
                onClick={handleSubmit}
                loading={loading}
                variant="danger"
              />
            )}
          </div>
        </div>
      )}
    </Form>
  );
};

export default PaymentCorrectionSection;
